import { createTheme, alpha } from "@mui/material";

/**
 * @typedef {import('@mui/material/styles').PaletteColor} PaletteColor
 * @typedef {import('@mui/material/styles').PaletteColorOptions} PaletteColorOptions
 * * @typedef {Object} CustomColorAdditions
 * @property {string} lighter
 * * @typedef {Object} CustomTextAdditions
 * @property {string} hover
 * @property {string} inverted
 * * @typedef {Object} CustomBackgroundAdditions
 * @property {string} main
 * @property {string} elevated
 * * @typedef {import('@mui/material/styles').Theme} Theme
 */

export function setupTheme(mode) {
  const lightPalette = {
    mode: "light",
    primary: {
      main: "#FFFFFF",
      contrastText: "#1a1a1a",
    },
    secondary: {
      main: "#2563eb",
      lighter: "#3b82f6",
      light: "#60a5fa",
    },
    text: {
      primary: "#1a1a1a",
      secondary: "#64748b",
      hover: "#2563eb",
      inverted: "#FFFFFF",
    },
    background: {
      default: "#FFFFFF",
      main: "#FFFFFF",
      paper: "#f8fafc",
      elevated: "#ffffff",
    },
    divider: "rgba(148, 163, 184, 0.12)",
  };

  const darkPalette = {
    mode: "dark",
    primary: {
      main: "#0a0a0a",
      contrastText: "#f8fafc",
    },
    secondary: {
      main: "#3b82f6",
      lighter: "#60a5fa",
      light: "#93c5fd",
    },
    text: {
      primary: "#f8fafc",
      secondary: "#94a3b8",
      hover: "#60a5fa",
      inverted: "#0a0a0a",
    },
    background: {
      default: "#0a0a0a",
      main: "#0a0a0a",
      paper: "#18181b",
      elevated: "#27272a",
    },
    divider: "rgba(148, 163, 184, 0.12)",
  };

  /** @type {any} */
  const palette = mode === 'light' ? lightPalette : darkPalette;

  const theme = createTheme({
    palette,
    shape: {
      borderRadius: 12
    },
    typography: {
      htmlFontSize: 10,
      fontFamily: "Nunito, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      h2: {
        fontSize: "28px",
        fontWeight: 700,
        letterSpacing: "-0.02em",
        lineHeight: 1.3,
      },
      h3: {
        fontSize: "24px",
        fontWeight: 700,
        letterSpacing: "-0.01em",
        lineHeight: 1.4,
      },
      h4: {
        fontSize: "20px",
        fontWeight: 600,
        letterSpacing: "-0.01em",
        lineHeight: 1.5,
      },
      h5: {
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "16px",
        lineHeight: 1.7,
        letterSpacing: "0.001em",
      },
      body2: {
        fontSize: "14px",
        lineHeight: 1.6,
        letterSpacing: "0.001em",
      },
      caption: {
        fontSize: "13px",
        lineHeight: 1.5,
        color: palette.text.secondary,
      },
    },
    shadows: [
      'none',
      '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      ...Array(19).fill('0 25px 50px -12px rgba(0, 0, 0, 0.25)')
    ],
  });

  theme.components = {
    MuiCssBaseline: {
      styleOverrides: `
          html {
            font-size: 62.5%;
          }

          body {
            background-color: ${palette.background.main};
            color: ${palette.text.primary};
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;

            ${mode === 'dark' ?
              `img:not(.no-invert) {
                  filter: invert(1) hue-rotate(180deg);
              }`
              : ''}
          }

          * {
            transition-property: background-color, border-color, color, fill, stroke;
            transition-duration: 200ms;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          }
      `,
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.primary.main, 0.6),
          backdropFilter: "blur(10px)",
          border: `1px solid ${alpha(palette.text.primary, 0.08)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: alpha(palette.secondary.main, 0.1),
            borderColor: alpha(palette.secondary.main, 0.2),
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.primary.main, 0.6),
          color: palette.text.primary,
          backdropFilter: "blur(10px)",
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 20px',
          border: `1px solid ${alpha(palette.text.primary, 0.08)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: alpha(palette.secondary.main, 0.1),
            borderColor: alpha(palette.secondary.main, 0.2),
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.main,
          color: palette.text.primary,
          border: `1px solid ${alpha(palette.text.primary, 0.08)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.08)',
            borderColor: alpha(palette.text.primary, 0.12),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          color: palette.text.primary,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '13px',
          height: '28px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          border: `1px solid ${alpha(palette.text.primary, 0.08)}`,
          '&:hover': {
            transform: 'translateY(-1px)',
            borderColor: alpha(palette.secondary.main, 0.3),
          },
        },
      },
    },
  };

  return theme;
}